import { useState, useMemo } from 'react';
import {
  Download,
  CreditCard,
  Banknote,
  Gift,
  Wallet,
  TrendingUp,
  Loader2,
  Printer,
  Globe,
  MapPin,
} from 'lucide-react';
import { DateRangeSelect, type DateRangeValue } from '@/components/ui/date-range-select';
import { datePresets, presetValue } from '@/lib/datePresets';

const DATE_RANGE_PRESETS = datePresets('today', 'week', 'last30', 'year');
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';
import { useLocations } from '@/hooks/useLocations';
import { useCurrency } from '@/hooks/useCurrency';
import { usePaymentTypes } from '@/hooks/usePaymentTypes';
import { useReportPrint } from '@/hooks/useReportPrint';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '@/components/ui/chart';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';

const PAYMENT_METHOD_CONFIG: Record<string, { icon: any; color: string; label: string }> = {
  cash: { icon: Banknote, color: 'hsl(142, 76%, 36%)', label: 'Cash' },
  card: { icon: CreditCard, color: 'hsl(217, 91%, 60%)', label: 'Card' },
  gift_card: { icon: Gift, color: 'hsl(263, 70%, 50%)', label: 'Gift Card' },
  loyalty: { icon: Wallet, color: 'hsl(25, 95%, 53%)', label: 'Loyalty' },
  store_credit: { icon: Wallet, color: 'hsl(330, 81%, 60%)', label: 'Store Credit' },
  other: { icon: CreditCard, color: 'hsl(215, 16%, 47%)', label: 'Other' },
};

const CHART_COLORS = [
  'hsl(142, 76%, 36%)',  // green
  'hsl(217, 91%, 60%)',  // blue
  'hsl(263, 70%, 50%)',  // purple
  'hsl(25, 95%, 53%)',   // orange
  'hsl(330, 81%, 60%)',  // pink
  'hsl(47, 96%, 53%)',   // yellow
  'hsl(174, 72%, 40%)',  // teal
];

export default function PaymentReports() {
  const { currentLocation } = useLocationStore();
  const { locations } = useLocations();
  const { formatCurrency } = useCurrency();
  const { paymentTypes } = usePaymentTypes();

  const [selectedLocationId, setSelectedLocationId] = useState<string>(currentLocation?.id || 'all');
  const [dateSel, setDateSel] = useState<DateRangeValue>(() => presetValue('week'));
  const { printRef, handlePrint } = useReportPrint({ title: 'Payment report' });

  // Determine which location to use
  const queryLocationId = selectedLocationId === 'all' ? currentLocation?.id : selectedLocationId;
  const selectedLocation = locations.find(l => l.id === selectedLocationId);

  // Calculate date range
  const dateFilters = useMemo(
    () => ({ start: dateSel.start.toISOString(), end: dateSel.end.toISOString() }),
    [dateSel]
  );

  const dateRangeLabel = useMemo(() => {
    const start = new Date(dateFilters.start);
    const end = new Date(dateFilters.end);
    return `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} to ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }, [dateFilters]);

  // Fetch payment data
  const { data: payments, isLoading } = useQuery({
    queryKey: ['payment-reports', queryLocationId, dateFilters],
    queryFn: async () => {
      if (!queryLocationId) return [];

      const { data, error } = await supabase
        .from('payments')
        .select(`
          id,
          payment_method,
          payment_type_id,
          amount,
          tip_amount,
          created_at,
          status,
          order:orders!inner(location_id, user_id)
        `)
        .eq('order.location_id', queryLocationId)
        .gte('created_at', dateFilters.start)
        .lte('created_at', dateFilters.end)
        .eq('status', 'completed');

      if (error) throw error;
      return data;
    },
    enabled: !!queryLocationId,
  });

  // Aggregate by payment type
  const paymentsByType = useMemo(() => {
    if (!payments) return [];

    const typeMap: Record<string, { amount: number; count: number; tips: number }> = {};

    payments.forEach(payment => {
      // Get label for payment type
      let label = payment.payment_method;

      // Check if it's a custom payment type
      if (payment.payment_type_id) {
        const customType = paymentTypes.find(pt => pt.id === payment.payment_type_id);
        if (customType) {
          label = customType.name;
        }
      } else {
        const config = PAYMENT_METHOD_CONFIG[payment.payment_method];
        label = config?.label || payment.payment_method;
      }

      if (!typeMap[label]) {
        typeMap[label] = { amount: 0, count: 0, tips: 0 };
      }
      typeMap[label].amount += payment.amount || 0;
      typeMap[label].count += 1;
      typeMap[label].tips += payment.tip_amount || 0;
    });

    return Object.entries(typeMap)
      .map(([name, data], index) => ({
        name,
        ...data,
        fill: CHART_COLORS[index % CHART_COLORS.length],
      }))
      .sort((a, b) => b.amount - a.amount);
  }, [payments, paymentTypes]);

  // Create chart config from payment types
  const chartConfig = useMemo(() => {
    const config: ChartConfig = {};
    paymentsByType.forEach((pt, index) => {
      config[pt.name] = {
        label: pt.name,
        color: CHART_COLORS[index % CHART_COLORS.length],
      };
    });
    return config;
  }, [paymentsByType]);

  // Aggregate by day
  const paymentsByDay = useMemo(() => {
    if (!payments) return [];

    const dayMap: Record<string, Record<string, number>> = {};

    payments.forEach(payment => {
      const date = new Date(payment.created_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });

      let label = PAYMENT_METHOD_CONFIG[payment.payment_method]?.label || payment.payment_method;

      if (payment.payment_type_id) {
        const customType = paymentTypes.find(pt => pt.id === payment.payment_type_id);
        if (customType) label = customType.name;
      }

      if (!dayMap[date]) {
        dayMap[date] = {};
      }
      if (!dayMap[date][label]) {
        dayMap[date][label] = 0;
      }
      dayMap[date][label] += payment.amount || 0;
    });

    return Object.entries(dayMap)
      .map(([date, types]) => ({
        date,
        ...types,
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [payments, paymentTypes]);

  // Calculate totals
  const totals = useMemo(() => {
    if (!payments) return { total: 0, count: 0, tips: 0 };

    return payments.reduce(
      (acc, p) => ({
        total: acc.total + (p.amount || 0),
        count: acc.count + 1,
        tips: acc.tips + (p.tip_amount || 0),
      }),
      { total: 0, count: 0, tips: 0 }
    );
  }, [payments]);

  // Export CSV
  const exportToCSV = () => {
    if (!paymentsByType || paymentsByType.length === 0) return;

    const headers = ['Payment Type', 'Transaction Count', 'Total Amount', 'Tips', 'Percentage'];

    const rows = paymentsByType.map(pt => [
      pt.name,
      pt.count,
      pt.amount.toFixed(2),
      pt.tips.toFixed(2),
      totals.total > 0 ? ((pt.amount / totals.total) * 100).toFixed(1) + '%' : '0%',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payment-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Controls Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          {selectedLocation?.name || currentLocation?.name || 'All Locations'} - Breakdown by payment type
        </p>
        <div className="flex flex-wrap items-center gap-3">
          {/* Location Selector */}
          <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select location" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <div className="flex items-center gap-2">
                  <Globe className="h-3.5 w-3.5" />
                  <span>All locations</span>
                </div>
              </SelectItem>
              {locations.length > 0 && <div className="my-1 h-px bg-border" />}
              {locations.map((loc) => (
                <SelectItem key={loc.id} value={loc.id}>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>{loc.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <DateRangeSelect presets={DATE_RANGE_PRESETS} value={dateSel} onChange={setDateSel} />
          <Button variant="outline" className="gap-2" onClick={handlePrint}>
            <Printer className="h-4 w-4" />
            Print
          </Button>
          <Button variant="outline" className="gap-2" onClick={exportToCSV}>
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Payments</span>
              <TrendingUp className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : formatCurrency(totals.total)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {totals.count} transactions
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Tips</span>
              <Wallet className="h-4 w-4 text-orange-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : formatCurrency(totals.tips)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Avg Transaction</span>
              <CreditCard className="h-4 w-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : formatCurrency(totals.count > 0 ? totals.total / totals.count : 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Payment Distribution</CardTitle>
              <CardDescription>Breakdown by payment method</CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-[300px]">
                <PieChart>
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        valueFormatter={(value) => formatCurrency(value)}
                        labelFormatter={(_, payload) => {
                          if (payload && payload.length > 0) {
                            const item = payload[0] as { payload?: { amount?: number; name?: string } };
                            const data = item.payload;
                            if (data) {
                              const percentage = totals.total > 0 && data.amount
                                ? ((data.amount / totals.total) * 100).toFixed(1)
                                : '0';
                              return `${data.name || ''} (${percentage}%)`;
                            }
                          }
                          return '';
                        }}
                      />
                    }
                  />
                  <Pie
                    data={paymentsByType}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="amount"
                    nameKey="name"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {paymentsByType.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <ChartLegend content={<ChartLegendContent nameKey="name" />} />
                </PieChart>
              </ChartContainer>
            </CardContent>
          </Card>

          {/* Bar Chart - Daily Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Daily Payments</CardTitle>
              <CardDescription>Payment amounts by day and type</CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-[300px]">
                <BarChart data={paymentsByDay}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="date"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    className="fill-muted-foreground"
                  />
                  <YAxis
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    className="fill-muted-foreground"
                    tickFormatter={(value) => `$${value}`}
                  />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        valueFormatter={(value) => formatCurrency(value)}
                      />
                    }
                  />
                  <ChartLegend content={<ChartLegendContent />} />
                  {paymentsByType.slice(0, 5).map((type, index) => (
                    <Bar
                      key={type.name}
                      dataKey={type.name}
                      stackId="a"
                      fill={CHART_COLORS[index % CHART_COLORS.length]}
                      radius={index === paymentsByType.slice(0, 5).length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ChartContainer>
            </CardContent>
          </Card>

          {/* Detailed Table */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Payment Type Breakdown</CardTitle>
              <CardDescription>Detailed view of all payment methods</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableHead>Payment Type</TableHead>
                    <TableHead className="text-right">Transactions</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Tips</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right w-[180px]">% of Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paymentsByType.map((pt) => {
                    const percentage = totals.total > 0 ? (pt.amount / totals.total) * 100 : 0;

                    return (
                      <TableRow key={pt.name}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div
                              className="w-3 h-3 rounded-full shrink-0"
                              style={{ backgroundColor: pt.fill }}
                            />
                            <span className="font-medium">{pt.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {pt.count.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatCurrency(pt.amount)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">
                          {formatCurrency(pt.tips)}
                        </TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {formatCurrency(pt.amount + pt.tips)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-3">
                            <Progress
                              value={percentage}
                              className="h-2 w-20"
                              style={{
                                ['--progress-background' as string]: pt.fill
                              }}
                            />
                            <span className="w-12 text-right tabular-nums text-muted-foreground">
                              {percentage.toFixed(1)}%
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell className="font-semibold">Total</TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {totals.count.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {formatCurrency(totals.total)}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {formatCurrency(totals.tips)}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {formatCurrency(totals.total + totals.tips)}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      100%
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Print template (hidden in UI) */}
      <div ref={printRef} className="print-source">
        <div className="print-header">
          <div>
            <h1>Payment report</h1>
            <div className="print-meta">
              {currentLocation?.name || 'All locations'} - {dateRangeLabel}
            </div>
          </div>
          <div className="print-meta">
            Generated on {new Date().toLocaleString()}
          </div>
        </div>

        <div className="print-summary">
          <div className="print-card">
            <div className="label">Total payments</div>
            <div className="value">{formatCurrency(totals.total)}</div>
            <div className="print-meta">{totals.count} transactions</div>
          </div>
          <div className="print-card">
            <div className="label">Total tips</div>
            <div className="value">{formatCurrency(totals.tips)}</div>
          </div>
          <div className="print-card">
            <div className="label">Average transaction</div>
            <div className="value">
              {totals.count > 0 ? formatCurrency(totals.total / totals.count) : formatCurrency(0)}
            </div>
          </div>
        </div>

        <div className="print-section">
          <h2>Payments by type</h2>
          {paymentsByType?.length ? (
            <table className="print-table">
              <thead>
                <tr>
                  <th>Payment type</th>
                  <th>Transactions</th>
                  <th>Amount</th>
                  <th>Tips</th>
                  <th>Total</th>
                  <th>% of total</th>
                </tr>
              </thead>
              <tbody>
                {paymentsByType.map((pt) => {
                  const percentage = totals.total > 0 ? (pt.amount / totals.total) * 100 : 0;
                  return (
                    <tr key={pt.name}>
                      <td>{pt.name}</td>
                      <td>{pt.count}</td>
                      <td>{formatCurrency(pt.amount)}</td>
                      <td>{formatCurrency(pt.tips)}</td>
                      <td>{formatCurrency(pt.amount + pt.tips)}</td>
                      <td>{percentage.toFixed(1)}%</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total</td>
                  <td>{totals.count}</td>
                  <td>{formatCurrency(totals.total)}</td>
                  <td>{formatCurrency(totals.tips)}</td>
                  <td>{formatCurrency(totals.total + totals.tips)}</td>
                  <td>100%</td>
                </tr>
              </tfoot>
            </table>
          ) : (
            <div className="print-meta">No payments recorded for this range.</div>
          )}
        </div>

        <div className="print-section">
          <h2>Payments by day</h2>
          {paymentsByDay?.length ? (
            <table className="print-table">
              <thead>
                <tr>
                  <th>Date</th>
                  {paymentsByType.map((pt) => (
                    <th key={pt.name}>{pt.name}</th>
                  ))}
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {paymentsByDay.map((day) => {
                  const rowTotal = paymentsByType.reduce((sum, pt) => sum + ((day as any)[pt.name] || 0), 0);
                  return (
                    <tr key={day.date}>
                      <td>{day.date}</td>
                      {paymentsByType.map((pt) => (
                        <td key={pt.name}>{formatCurrency((day as any)[pt.name] || 0)}</td>
                      ))}
                      <td>{formatCurrency(rowTotal)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="print-meta">No daily breakdown available.</div>
          )}
        </div>
      </div>
    </div>
  );
}
